/**
 * Client Detail Page
 *
 * Displays detailed information about a single client.
 *
 * @module pages/clients/ClientDetail
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  ListGroup,
  Dropdown,
  Modal,
} from 'react-bootstrap';
import {
  getClient,
  getClientStats,
  addClientContact,
  updateClientContact,
  removeClientContact,
} from '../../services/clientService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../utils/toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ClientContactModal from '../../components/clients/ClientContactModal';
import KebabMenu from '../../components/common/KebabMenu';

/**
 * Format client type for display
 */
const formatClientType = (type) => {
  // Las 8 claves son el allowlist de `clients_client_type_check`. `regular` y
  // `vip` faltaban y existen en datos de producción: salían con el código
  // crudo y el ícono de interrogación. Mantener en sync con la copia de
  // ClientList.js y con CLIENT_TYPE_OPTIONS en ClientForm.js.
  const types = {
    regular: { label: 'Regular', variant: 'secondary', icon: 'bi-building' },
    vip: { label: 'VIP', variant: 'dark', icon: 'bi-star-fill' },
    hospital: { label: 'Hospital', variant: 'danger', icon: 'bi-hospital' },
    clinic: { label: 'Clínica', variant: 'primary', icon: 'bi-plus-circle' },
    laboratory: { label: 'Laboratorio', variant: 'info', icon: 'bi-droplet' },
    pharmacy: { label: 'Farmacia', variant: 'success', icon: 'bi-capsule' },
    distributor: { label: 'Distribuidor', variant: 'warning', icon: 'bi-truck' },
    other: { label: 'Otro', variant: 'secondary', icon: 'bi-building' },
  };
  return types[type] || { label: type || 'Sin tipo', variant: 'secondary', icon: 'bi-question-circle' };
};

/**
 * Format client status for display
 */
const formatClientStatus = (status) => {
  const statuses = {
    active: { label: 'Activo', variant: 'success' },
    inactive: { label: 'Inactivo', variant: 'secondary' },
    suspended: { label: 'Suspendido', variant: 'danger' },
  };
  return statuses[status] || { label: status || 'Desconocido', variant: 'secondary' };
};

const ClientDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { isAdmin, isTechnician, user } = useAuth();
  const queryClient = useQueryClient();

  const [showContactModal, setShowContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);

  // Fetch client data
  const {
    data: clientData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['client', id],
    queryFn: () => getClient(id),
    enabled: Boolean(id),
  });

  // Fetch client stats
  const { data: statsData } = useQuery({
    queryKey: ['clientStats', id],
    queryFn: () => getClientStats(id),
    enabled: Boolean(id),
  });

  const invalidateClient = () => queryClient.invalidateQueries(['client', id]);

  const addContactMutation = useMutation({
    mutationFn: (payload) => {
      const { user_id, ...contactData } = payload;
      // `created_by` is required by the employee_create_client_contacts /
      // technician_create_client_contacts RLS policies (WITH CHECK created_by
      // = auth.uid()); admins bypass via admin_all_client_contacts but we set
      // it unconditionally for correctness and audit trail.
      return addClientContact(id, user_id, { ...contactData, created_by: user?.id });
    },
    onSuccess: ({ error: mutationError }) => {
      if (mutationError) throw mutationError;
      toast.success('Contacto agregado');
      invalidateClient();
      setShowContactModal(false);
      setEditingContact(null);
    },
    onError: (mutationError) => {
      toast.error('Error al agregar contacto: ' + mutationError.message);
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: ({ contactId, payload }) => {
      const { user_id, ...contactData } = payload;
      return updateClientContact(contactId, { user_id, ...contactData });
    },
    onSuccess: ({ error: mutationError }) => {
      if (mutationError) throw mutationError;
      toast.success('Contacto actualizado');
      invalidateClient();
      setShowContactModal(false);
      setEditingContact(null);
    },
    onError: (mutationError) => {
      toast.error('Error al actualizar contacto: ' + mutationError.message);
    },
  });

  const removeContactMutation = useMutation({
    mutationFn: (contactId) => removeClientContact(contactId),
    onSuccess: ({ error: mutationError }) => {
      if (mutationError) throw mutationError;
      toast.success('Contacto eliminado');
      invalidateClient();
      setContactToDelete(null);
    },
    onError: (mutationError) => {
      toast.error('Error al eliminar contacto: ' + mutationError.message);
    },
  });

  const handleOpenAddContact = () => {
    setEditingContact(null);
    setShowContactModal(true);
  };

  const handleOpenEditContact = (contact) => {
    setEditingContact(contact);
    setShowContactModal(true);
  };

  const handleSaveContact = (payload) => {
    if (editingContact) {
      updateContactMutation.mutate({ contactId: editingContact.id, payload });
    } else {
      addContactMutation.mutate(payload);
    }
  };

  // El técnico da de alta contactos: en sitio conoce al encargado de planta y
  // le anota el teléfono. Eso se conserva (#537).
  const canManageContacts = isAdmin || isTechnician;

  // Editar, en cambio, sólo lo que él mismo cargó. La RLS ya lo impone
  // (`client_contacts_ceiling_update`, migración 20260729170000); esto es el
  // otro lado del mismo cambio: sin él, el técnico ve un "Editar" sobre el
  // contacto del admin que al guardar afecta 0 filas y no dice por qué.
  const canEditContact = (contact) => isAdmin || contact?.created_by === user?.id;

  // Borrar, nadie más que el admin. Regla del dueño del producto: los datos
  // maestros sólo los borra el admin — y `client_contacts` es el join
  // usuario-de-portal ↔ cliente, así que borrar una fila no borra un teléfono,
  // LE APAGA LA CUENTA a un cliente. El grant de RLS se quitó (no se tapó), y
  // el botón se va con él: un botón que siempre falla es peor que el bug.
  const canDeleteContacts = isAdmin;

  if (isLoading) {
    return (
      <DashboardLayout>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">{t('common.loading', 'Cargando...')}</p>
        </Container>
      </DashboardLayout>
    );
  }

  if (error || !clientData?.data) {
    return (
      <DashboardLayout>
        <Container className="py-5">
          <Alert variant="danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {t('clients.notFound', 'Cliente no encontrado')}
            <Button variant="link" onClick={() => navigate('/clientes')}>
              {t('common.backToList', 'Volver a la lista')}
            </Button>
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  const client = clientData.data;
  const stats = statsData?.data || {};
  // `client_type` primero: es la columna que la lista pinta en "Tipo" y por la
  // que filtra, y donde vive el dato en casi todas las filas de producción. El
  // orden inverso hacía que el detalle mostrara un tipo distinto al de la lista
  // para el mismo cliente. `industry` queda de respaldo para las filas viejas
  // creadas desde la web, que sólo tienen esa.
  const typeInfo = formatClientType(client.client_type || client.industry);
  const statusInfo = formatClientStatus(client.status);

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-start">
              <div className="d-flex align-items-center">
                <Button
                  variant="link"
                  className="p-0 me-3"
                  onClick={() => navigate('/clientes')}
                >
                  <i className="bi bi-arrow-left fs-4"></i>
                </Button>
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h2 className="mb-0">{client.company_name}</h2>
                    <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>
                  </div>
                  <div className="text-muted">
                    <Badge bg={typeInfo.variant} className="me-2">
                      <i className={`bi ${typeInfo.icon} me-1`}></i>
                      {typeInfo.label}
                    </Badge>
                    {client.city && client.country && (
                      <span>
                        <i className="bi bi-geo-alt me-1"></i>
                        {client.city}, {client.country}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {(isAdmin || isTechnician) && (
                <Button
                  variant="primary"
                  onClick={() => navigate(`/clientes/${id}/editar`)}
                >
                  <i className="bi bi-pencil me-2"></i>
                  {t('common.edit', 'Editar')}
                </Button>
              )}
            </div>
          </Col>
        </Row>

        <Row>
          {/* Main Content */}
          <Col lg={8}>
            {/* Contact Information */}
            <Card className="mb-4 shadow-sm">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="bi bi-person me-2"></i>
                  {t('clients.contactInfo', 'Información de Contacto')}
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small d-block">
                        {t('clients.contactName', 'Nombre del Contacto')}
                      </label>
                      <span className="fs-5">
                        {client.primary_contact_name || '-'}
                      </span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="text-muted small d-block">
                        {t('clients.contactPhone', 'Teléfono')}
                      </label>
                      <span className="fs-5">
                        {client.primary_contact_phone ? (
                          <a href={`tel:${client.primary_contact_phone}`}>
                            <i className="bi bi-telephone me-1"></i>
                            {client.primary_contact_phone}
                          </a>
                        ) : '-'}
                      </span>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="mb-3">
                      <label className="text-muted small d-block">
                        {t('clients.contactEmail', 'Email')}
                      </label>
                      <span className="fs-5">
                        {client.primary_contact_email ? (
                          <a href={`mailto:${client.primary_contact_email}`}>
                            <i className="bi bi-envelope me-1"></i>
                            {client.primary_contact_email}
                          </a>
                        ) : '-'}
                      </span>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Address */}
            <Card className="mb-4 shadow-sm">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="bi bi-geo-alt me-2"></i>
                  {t('clients.address', 'Dirección')}
                </h5>
              </Card.Header>
              <Card.Body>
                <p className="mb-0 fs-5">
                  {client.address_line1 || t('clients.noAddress', 'Sin dirección registrada')}
                </p>
                {(client.city || client.country) && (
                  <p className="text-muted mb-0">
                    {[client.city, client.country].filter(Boolean).join(', ')}
                  </p>
                )}
              </Card.Body>
            </Card>

            {/* Notes */}
            {client.notes && (
              <Card className="mb-4 shadow-sm">
                <Card.Header>
                  <h5 className="mb-0">
                    <i className="bi bi-journal-text me-2"></i>
                    {t('clients.notes', 'Notas')}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    {client.notes}
                  </p>
                </Card.Body>
              </Card>
            )}

            {/* Additional Contacts */}
            {(canManageContacts || (client.contacts && client.contacts.length > 0)) && (
              <Card className="mb-4 shadow-sm">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-people me-2"></i>
                    {t('clients.additionalContacts', 'Contactos Adicionales')}
                  </h5>
                  {canManageContacts && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      style={{ minHeight: 44 }}
                      onClick={handleOpenAddContact}
                    >
                      <i className="bi bi-plus-lg me-1"></i>
                      {t('clients.addContact', 'Agregar Contacto')}
                    </Button>
                  )}
                </Card.Header>
                {client.contacts && client.contacts.length > 0 ? (
                  <ListGroup variant="flush">
                    {client.contacts.map((contact) => {
                      const isLightweight = !contact.user_id;
                      const name = isLightweight ? contact.contact_name : contact.user?.full_name;
                      return (
                        <ListGroup.Item key={contact.id}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong>{name || '-'}</strong>
                              {isLightweight && (
                                <Badge bg="secondary" className="ms-2">
                                  {t('clients.noAccount', 'Sin cuenta')}
                                </Badge>
                              )}
                              {contact.contact_role && (
                                <>
                                  <br />
                                  <small className="text-muted text-capitalize">{contact.contact_role}</small>
                                </>
                              )}
                              {(contact.job_title || contact.department) && (
                                <>
                                  <br />
                                  <small className="text-muted">
                                    {[contact.job_title, contact.department].filter(Boolean).join(' · ')}
                                  </small>
                                </>
                              )}
                              {isLightweight && contact.contact_phone && (
                                <>
                                  <br />
                                  <small>
                                    <a href={`tel:${contact.contact_phone}`}>
                                      <i className="bi bi-telephone me-1"></i>
                                      {contact.contact_phone}
                                    </a>
                                  </small>
                                </>
                              )}
                              {isLightweight && contact.contact_email && (
                                <>
                                  {' '}
                                  <small>
                                    <a href={`mailto:${contact.contact_email}`}>
                                      <i className="bi bi-envelope me-1"></i>
                                      {contact.contact_email}
                                    </a>
                                  </small>
                                </>
                              )}
                            </div>
                            {(canEditContact(contact) || canDeleteContacts) && (
                              <KebabMenu
                                label={`Acciones de ${name || 'contacto sin nombre'}`}
                                toggleStyle={{ minHeight: 44, minWidth: 44 }}
                              >
                                {canEditContact(contact) && (
                                  <Dropdown.Item onClick={() => handleOpenEditContact(contact)}>
                                    <i className="bi bi-pencil me-2"></i>
                                    {t('common.edit', 'Editar')}
                                  </Dropdown.Item>
                                )}
                                {canDeleteContacts && (
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => setContactToDelete(contact)}
                                  >
                                    <i className="bi bi-trash me-2"></i>
                                    {t('common.delete', 'Eliminar')}
                                  </Dropdown.Item>
                                )}
                              </KebabMenu>
                            )}
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                ) : (
                  <Card.Body>
                    <p className="text-muted mb-0">
                      {t('clients.noContacts', 'Sin contactos adicionales registrados.')}
                    </p>
                  </Card.Body>
                )}
              </Card>
            )}
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            {/* Statistics */}
            <Card className="mb-4 shadow-sm">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="bi bi-graph-up me-2"></i>
                  {t('clients.statistics', 'Estadísticas')}
                </h5>
              </Card.Header>
              <Card.Body>
                <Row className="g-3 text-center">
                  <Col xs={6}>
                    <div className="border rounded p-3">
                      <div className="fs-3 fw-bold text-primary">
                        {stats.total_projects || 0}
                      </div>
                      <small className="text-muted">
                        {t('clients.totalProjects', 'Proyectos')}
                      </small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="border rounded p-3">
                      <div className="fs-3 fw-bold text-success">
                        {stats.active_projects || 0}
                      </div>
                      <small className="text-muted">
                        {t('clients.activeProjects', 'Activos')}
                      </small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="border rounded p-3">
                      <div className="fs-3 fw-bold text-info">
                        {stats.total_quotations || 0}
                      </div>
                      <small className="text-muted">
                        {t('clients.quotations', 'Cotizaciones')}
                      </small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="border rounded p-3">
                      <div className="fs-3 fw-bold text-warning">
                        ${(stats.total_revenue || 0).toLocaleString()}
                      </div>
                      <small className="text-muted">
                        {t('clients.revenue', 'Facturado')}
                      </small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Quick Actions */}
            <Card className="mb-4 shadow-sm">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="bi bi-lightning me-2"></i>
                  {t('common.quickActions', 'Acciones Rápidas')}
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button
                    variant="outline-primary"
                    onClick={() => navigate(`/proyectos?client=${id}`)}
                  >
                    <i className="bi bi-folder me-2"></i>
                    {t('clients.viewProjects', 'Ver Proyectos')}
                  </Button>
                  <Button
                    variant="outline-success"
                    onClick={() => navigate(`/proyectos/nuevo?client=${id}`)}
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    {t('clients.newProject', 'Nuevo Proyecto')}
                  </Button>
                </div>
              </Card.Body>
            </Card>

            {/* Metadata */}
            <Card className="shadow-sm">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  {t('common.info', 'Información')}
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <small className="text-muted d-block">
                    {t('common.createdAt', 'Creado')}
                  </small>
                  <span>
                    {new Date(client.created_at).toLocaleDateString('es-CR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
                {client.created_by_profile && (
                  <div className="mb-2">
                    <small className="text-muted d-block">
                      {t('common.createdBy', 'Creado por')}
                    </small>
                    <span>{client.created_by_profile.full_name}</span>
                  </div>
                )}
                {client.updated_at && (
                  <div>
                    <small className="text-muted d-block">
                      {t('common.updatedAt', 'Última actualización')}
                    </small>
                    <span>
                      {new Date(client.updated_at).toLocaleDateString('es-CR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <ClientContactModal
        show={showContactModal}
        onHide={() => {
          setShowContactModal(false);
          setEditingContact(null);
        }}
        onSave={handleSaveContact}
        contact={editingContact}
        loading={addContactMutation.isPending || updateContactMutation.isPending}
      />

      <Modal show={Boolean(contactToDelete)} onHide={() => setContactToDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('clients.deleteContact', 'Eliminar Contacto')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('clients.deleteContactConfirm', {
            name:
              contactToDelete?.contact_name ||
              contactToDelete?.user?.full_name ||
              'este contacto',
            defaultValue:
              '¿Seguro que deseas eliminar a {{name}} como contacto de este cliente?',
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setContactToDelete(null)}
            disabled={removeContactMutation.isPending}
            style={{ minHeight: 44 }}
          >
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            variant="danger"
            onClick={() => removeContactMutation.mutate(contactToDelete.id)}
            disabled={removeContactMutation.isPending}
            style={{ minHeight: 44 }}
          >
            {removeContactMutation.isPending ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : (
              t('common.delete', 'Eliminar')
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </DashboardLayout>
  );
};

export default ClientDetail;
